<template>
  <a-drawer
    title="平台 Cookie 配置"
    width="700"
    :visible="visible"
    :closable="false"
    :footer-style="{ textAlign: 'right' }"
    @close="touchClose"
  >
    <!-- 列表头部：新建按钮 -->
    <div style="display: flex; justify-content: flex-end; margin-bottom: 12px;">
      <a-button type="primary" @click="touchNew">新建</a-button>
    </div>
    <!-- Cookie 列表 -->
    <a-table
      :data-source="dataSource"
      :columns="columns"
      :row-key="record => record.id"
      :loading="isLoading"
      :pagination="false"
    >
      <template #bodyCell="{ column, record }">
        <!-- 创建时间 -->
        <template v-if="column.key === 'created_at'">
          <span>{{ formatTime(record.created_at) }}</span>
        </template>
        <!-- 操作 -->
        <template v-else-if="column.key === 'operation'">
          <a @click="touchEdit(record)">编辑</a>
        </template>
      </template>
    </a-table>
    <!-- footer -->
    <template #footer>
      <a-button @click="touchClose">关闭</a-button>
    </template>
  </a-drawer>

  <!-- 新建/编辑 Modal -->
  <a-modal
    :title="editForm.id ? '编辑 Cookie' : '新建 Cookie'"
    :visible="editVisible"
    :confirm-loading="isSubmitting"
    ok-text="保存"
    cancel-text="取消"
    @ok="touchSave"
    @cancel="touchEditCancel"
  >
    <a-spin :spinning="isDetailLoading">
    <a-form
      ref="RefEditForm"
      :model="editForm"
      :label-col="{ span: 5 }"
      :wrapper-col="{ span: 17 }"
      style="margin-top: 16px;"
    >
      <!-- 平台类型 -->
      <a-form-item label="平台" name="type" :rules="[{ required: true, message: '请选择平台' }]">
        <a-select
          v-model:value="editForm.type"
          placeholder="请选择平台"
          allow-clear
          :getPopupContainer="(t) => t.parentNode"
          @change="onTypeChange"
        >
          <a-select-option v-for="item in PlatformList" :key="item.key" :value="item.key">{{ item.text }}</a-select-option>
        </a-select>
      </a-form-item>
      <!-- Cookie 输入项（UC：真人 + 漫剧） -->
      <template v-if="editForm.type === PlatformKeyUc">
        <a-form-item label="真人 Cookie" name="cookie" :rules="[{ message: '请输入真人 Cookie' }]">
          <a-textarea v-model:value="editForm.cookie" placeholder="请输入真人 Cookie" :rows="4" allow-clear />
        </a-form-item>
        <a-form-item label="漫剧 Cookie" name="mj_cookie" :rules="[{ message: '请输入漫剧 Cookie' }]">
          <a-textarea v-model:value="editForm.mj_cookie" placeholder="请输入漫剧 Cookie" :rows="4" allow-clear />
        </a-form-item>
      </template>
      <!-- Cookie 输入项（支付宝：男频 + 女频） -->
      <template v-if="editForm.type === PlatformKeyZfb">
        <a-form-item label="男频 Cookie" name="male_cookie" :rules="[{ message: '请输入男频 Cookie' }]">
          <a-textarea v-model:value="editForm.male_cookie" placeholder="请输入男频 Cookie" :rows="4" allow-clear />
        </a-form-item>
        <a-form-item label="女频 Cookie" name="female_cookie" :rules="[{ message: '请输入女频 Cookie' }]">
          <a-textarea v-model:value="editForm.female_cookie" placeholder="请输入女频 Cookie" :rows="4" allow-clear />
        </a-form-item>
      </template>
      <!-- 其他平台：通用单个 Cookie -->
      <template v-if="editForm.type && editForm.type !== PlatformKeyUc && editForm.type !== PlatformKeyZfb">
        <a-form-item label="Cookie" name="cookie" :rules="[{ message: '请输入 Cookie' }]">
          <a-textarea v-model:value="editForm.cookie" placeholder="请输入 Cookie" :rows="4" allow-clear />
        </a-form-item>
      </template>
    </a-form>
    </a-spin>
  </a-modal>
</template>

<script setup>
import { ref, reactive } from 'vue'
import { message } from 'ant-design-vue'
import { thirdCookieList, thirdCookieDetail, thirdCookieSave } from '@/api/copyright'
import { ThirdPlatformList, PlatformKeyUc, PlatformKeyZfb } from '@/utils/constantFilmLibrary'

const PlatformList = ThirdPlatformList

// ========================= Refs =========================
const RefEditForm = ref(null)
const visible = ref(false)
const isLoading = ref(false)
const isDetailLoading = ref(false)
const isSubmitting = ref(false)
const editVisible = ref(false)

// 列表数据
const dataSource = ref([])

// 编辑表单
const editForm = reactive({
  id: null,
  type: undefined,
  name: '',
  cookie: '',
  mj_cookie: '',
  male_cookie: '',
  female_cookie: ''
})

// ========================= 列配置 =========================
const columns = [
  { title: 'ID', dataIndex: 'id', key: 'id', width: 60 },
  { title: '名称', dataIndex: 'name', key: 'name', width: 120 },
  { title: '平台', dataIndex: 'type', key: 'type', width: 120 },
  { title: '创建时间', dataIndex: 'created_at', key: 'created_at', width: 180 },
  { title: '操作', dataIndex: 'operation', key: 'operation', width: 80 }
]

// ========================= 工具函数 =========================
function getPlatformText(key) {
  const item = PlatformList.find(p => p.key === key)
  return item ? item.text : key || '-'
}

function formatTime(timeStr) {
  if (!timeStr) return '-'
  return timeStr.replace('T', ' ').replace('.000000Z', '')
}

function resetEditForm() {
  RefEditForm.value?.resetFields()
  editForm.id = null
  editForm.type = undefined
  editForm.name = ''
  editForm.cookie = ''
  editForm.mj_cookie = ''
  editForm.male_cookie = ''
  editForm.female_cookie = ''
}

// ========================= 暴露方法 =========================
function show() {
  visible.value = true
  getCookieList()
}

defineExpose({ show })

// ========================= 事件处理 =========================
function touchClose() {
  visible.value = false
  dataSource.value = []
  resetEditForm()
}

function touchNew() {
  resetEditForm()
  editVisible.value = true
}

function touchEdit(record) {
  resetEditForm()
  editVisible.value = true
  getDetail(record.type)
}

function touchEditCancel() {
  editVisible.value = false
  resetEditForm()
}

function onTypeChange(val) {
  const item = PlatformList.find(p => p.key === val)
  editForm.name = item ? item.text : ''
}

function touchSave() {
  RefEditForm.value?.validate().then(() => {
    submitSave()
  }).catch(() => {})
}

// ========================= 接口 =========================
function getDetail(type) {
  isDetailLoading.value = true
  thirdCookieDetail({ type }).then(res => {
    isDetailLoading.value = false
    const { code, data } = res
    if (code === 0 && data) {
      editForm.id = data.id || null
      editForm.type = data.type || undefined
      editForm.name = data.name || ''
      editForm.cookie = data.cookie || ''
      editForm.mj_cookie = data.mj_cookie || ''
      editForm.male_cookie = data.male_cookie || ''
      editForm.female_cookie = data.female_cookie || ''
    } else {
      message.error(res.msg || res.message || '获取详情失败')
    }
  }).catch(() => {
    isDetailLoading.value = false
    message.error('获取详情失败')
  })
}

function getCookieList() {
  isLoading.value = true
  thirdCookieList({}).then(res => {
    isLoading.value = false
    const { code, data } = res
    if (code === 0) {
      dataSource.value = data || []
    } else {
      message.error(res.msg || res.message || '获取列表失败')
    }
  }).catch(() => {
    isLoading.value = false
    message.error('获取列表失败')
  })
}

function submitSave() {
  isSubmitting.value = true
  const params = {
    type: editForm.type,
    name: editForm.name,
    cookie: editForm.cookie,
    mj_cookie: editForm.mj_cookie,
    male_cookie: editForm.male_cookie,
    female_cookie: editForm.female_cookie
  }
  if (editForm.id) {
    params.id = editForm.id
  }
  thirdCookieSave(params).then(res => {
    isSubmitting.value = false
    if (res.code === 0) {
      message.success('保存成功')
      editVisible.value = false
      resetEditForm()
      getCookieList()
    } else {
      message.error(res.msg || res.message || '保存失败')
    }
  }).catch(() => {
    isSubmitting.value = false
    message.error('保存失败')
  })
}
</script>
